import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Button, Col, Label, Row } from 'reactstrap';
import CardWithTitle from 'components/CardWithTitle';
import productImg from 'assets/product-img.png';
import { splitNameFromUrl } from 'helper/utils';
import { useQuery } from '@apollo/client';
import { FSBTable } from 'components/common/FSBTable';
import { showSequenceNumber } from 'utils/constants';
import { SECTOR_FAVOURITED_BY_USERS } from 'pages/Products/ProductsGraphQL';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setVendorDetails } from 'actions/vendorDetailsAction';
import 'pages/Products/components/ProductDetailsMobile.css';

const renderClickableUserName = (value, traderId, row, onVendorOpen) => {
  if (!value || !value.trim()) return '-';
  if (!traderId) return value;
  return (
    <div
      className="text-blue-c5 fw-medium cursor-pointer text-nowrap"
      onClick={() => onVendorOpen(row)}
      aria-hidden
    >
      {value}
    </div>
  );
};

const SubCategoryDetails = ({ subCategoryDetails }) => {
  const [userFilters, setUserFilters] = useState({
    pageNo: 1,
    pageSize: 10,
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const sectorId = subCategoryDetails?.id;
  const {
    data: favouritedData,
    loading: favouritedLoading,
    error: favouritedError,
  } = useQuery(SECTOR_FAVOURITED_BY_USERS, {
    variables: {
      sectorId,
      pageNo: userFilters.pageNo,
      pageSize: userFilters.pageSize,
    },
    skip: !sectorId,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    setUserFilters((prev) => ({
      ...prev,
      pageNo: 1,
    }));
  }, [sectorId]);

  const getFirstFile = (value) => {
    if (!value) return '';
    return Array.isArray(value) ? value[0] : value;
  };

  const downloadFile = async (fileUrl, fileName) => {
    if (!fileUrl) return;
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const photoUrl = getFirstFile(subCategoryDetails?.photo);
  const documentUrl = getFirstFile(subCategoryDetails?.document);
  const documentName = documentUrl ? splitNameFromUrl(documentUrl) : '';

  const handleVendorOpen = useCallback(
    (row) => {
      const rawTraderId = row?.traderId;
      if (!rawTraderId) return;
      const vendorId = parseInt(rawTraderId, 10);
      if (Number.isNaN(vendorId)) return;
      dispatch(
        setVendorDetails({
          status: true,
          selectedVendor: {
            id: vendorId,
            entityName: row?.entityName || row?.username || '',
          },
        }),
      );
      navigate('/vendors');
    },
    [dispatch, navigate],
  );

  const userColumns = useMemo(
    () => [
      {
        accessorKey: 'SNo',
        header: 'S.NO.',
        enableSorting: false,
        cell: (cell) => showSequenceNumber(cell, userFilters),
      },
      {
        accessorKey: 'traderId',
        header: 'TRADER ID',
        enableSorting: false,
        cell: ({ row }) => row?.original?.traderId || '-',
      },
      {
        accessorKey: 'username',
        header: 'USER NAME',
        enableSorting: false,
        cell: ({ row }) =>
          renderClickableUserName(
            row?.original?.username,
            row?.original?.traderId,
            row?.original,
            handleVendorOpen,
          ),
      },
      {
        accessorKey: 'entityName',
        header: 'Company Name',
        enableSorting: false,
        cell: ({ row }) => row?.original?.entityName || '-',
      },
      {
        accessorKey: 'phoneNumber',
        header: 'PHONE NUMBER',
        enableSorting: false,
        cell: ({ row }) => row?.original?.phoneNumber || '-',
      },
    ],
    [userFilters, handleVendorOpen],
  );

  const handleUserPageChange = ({ pageSize, pageNo }) => {
    setUserFilters((prev) => ({
      ...prev,
      pageNo,
      pageSize,
    }));
  };

  const users = favouritedData?.sectorFavouritedByUsers?.users || [];
  const usersTotalCount =
    favouritedData?.sectorFavouritedByUsers?.totalCount || 0;
  const usersTotalPages =
    favouritedData?.sectorFavouritedByUsers?.totalPages || 0;

  return (
    <>
      <CardWithTitle title="General Details" className="product-detail-card">
        <Row className="mb-2 align-items-center product-detail-grid">
          <Col xs={4} xl={3}>
            <div className="product-info product-detail-media d-flex align-items-center">
              <img src={photoUrl || productImg} alt="sector" />
              <div className="ms-3 ms-xxl-4">
                <Label className="f-12 text-uppercase fw-medium text-gray  d-block mb-0">
                  Sector
                </Label>
                <p className="f-14 mb-0 text-capitalize text-break product-detail-value">
                  {subCategoryDetails?.name || 'Not Available'}
                </p>
              </div>
            </div>
          </Col>
          <Col xs={3} xl={2}>
            <Label className="f-12 text-uppercase fw-medium text-gray d-block mb-0">
              Product
            </Label>
            <p className="f-12 mb-0 text-capitalize product-detail-value">
              {subCategoryDetails?.product?.name || 'Not Available'}
            </p>
          </Col>
          <Col xs={3} xl={2}>
            <Label className="f-12 text-uppercase fw-medium text-gray d-block mb-0">
              Polymer Sector
            </Label>
            <p className="f-12 mb-0 text-capitalize product-detail-value">
              {subCategoryDetails?.productSubCategory || 'Not Available'}
            </p>
          </Col>
          <Col xs={3} xl={2}>
            <Label className="f-12 text-uppercase fw-medium text-gray d-block mb-0">
              Published On
            </Label>
            <p className="f-12 mb-0 product-detail-value">
              {subCategoryDetails?.createdAt
                ? moment(subCategoryDetails.createdAt).format(
                    'D MMM, YYYY HH:mm A',
                  )
                : 'Not Available'}
            </p>
          </Col>
          <Col xs={3} xl={1}>
            <Label className="f-12 text-uppercase fw-medium text-gray d-block mb-0">
              Status
            </Label>
            <p className="f-12 mb-0 product-detail-value">
              {subCategoryDetails?.status || 'Not Available'}
            </p>
          </Col>
          <Col xs={3} xl={2}>
            <Label className="f-12 text-uppercase fw-medium text-gray d-block mb-0">
              Documents
            </Label>
            {documentUrl ? (
              <Button
                color="link"
                className="p-0 f-12 text-decoration-none text-start product-detail-value"
                onClick={() => downloadFile(documentUrl, documentName)}
              >
                {documentName}
              </Button>
            ) : (
              <p className="f-12 mb-0 product-detail-value">Not Available</p>
            )}
          </Col>
        </Row>
      </CardWithTitle>

      <CardWithTitle
        title="Sector Favourited By Users"
        className="product-detail-card product-detail-favourites"
      >
        {favouritedError ? (
          <p className="f-12 text-danger mb-2">{favouritedError.message}</p>
        ) : null}
        <FSBTable
          data={users}
          totalCount={usersTotalCount}
          totalPages={usersTotalPages}
          columns={userColumns}
          currentPageNo={userFilters.pageNo}
          pageSize={userFilters.pageSize}
          keyField="traderId"
          showSearch={false}
          showFilters={false}
          showDelete={false}
          showPagination
          remote
          loading={favouritedLoading}
          striped={false}
          hover={false}
          bordered={false}
          wrapperClass="table-responsive word-break"
          handlePageChange={handleUserPageChange}
        />
      </CardWithTitle>
    </>
  );
};

export default SubCategoryDetails;
